import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { get_task_time_stats } from '../../../utils/talent_services';
import { formatoDuracion } from '../../../utils/talent_services';
import { subDays, format } from 'date-fns';
import { Card } from 'react-bootstrap';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const PERIOD_OPTIONS = [
  { value: 7, label: '7 días' },
  { value: 15, label: '15 días' },
  { value: 30, label: '30 días' },
  { value: 60, label: '60 días' },
];

function generateVibrantColors(count) {
  const colors = [];
  const saturation = 75;
  const lightness = 55;
  for (let i = 0; i < count; i++) {
    const hue = (i * 360) / Math.max(count, 1);
    colors.push(`hsla(${hue}, ${saturation}%, ${lightness}%, 0.85)`);
  }
  return colors;
}

/**
 * Gráfico de torta reutilizable que muestra el porcentaje de tiempo dedicado a cada tarea.
 * @param {Object} props
 * @param {string} props.token - Token de autenticación
 * @param {number} props.usrId - ID de usuario (para Dashboard - mis tareas). Opcional si se usan taskIds.
 * @param {number[]} props.taskIds - IDs de tareas filtradas (para Task_progress). Opcional.
 * @param {string} props.title - Título del gráfico
 * @param {number} props.defaultDays - Días por defecto del período (default: 15)
 */
function TaskTimePieChart({
  token,
  usrId = null,
  taskIds = null,
  title = 'Tiempo por tarea',
  defaultDays = 15,
  periodDays = null,
  showPeriodSelector = true,
  selectedTaskId = null,
  onSelectTaskId = null,
}) {
  const [stats, setStats] = useState([]);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalPeriodDays, setInternalPeriodDays] = useState(defaultDays);
  const effectivePeriodDays = periodDays ?? internalPeriodDays;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const endDate = new Date();
    const startDate = subDays(endDate, effectivePeriodDays);
    const dateFrom = format(startDate, 'yyyy-MM-dd 00:00:00');
    const dateTo = format(endDate, 'yyyy-MM-dd 23:59:59');

    get_task_time_stats(token, dateFrom, dateTo, usrId, taskIds)
      .then((data) => {
        if (cancelled) return;
        if (data === null || data === 'INVALID') {
          setError('No se pudieron cargar las estadísticas');
          setStats([]);
          setTotalMinutes(0);
        } else {
          setStats(data.stats || []);
          setTotalMinutes(data.total_minutes || 0);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError('Error al cargar las estadísticas');
          setStats([]);
          setTotalMinutes(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, usrId, effectivePeriodDays, JSON.stringify(taskIds || [])]);

  const chartColors = generateVibrantColors(stats.length);
  const labels = stats.map((s) => {
    const label = s.task_name || 'Tarea sin nombre';
    return label.length > 25 ? label.substring(0, 25) + '…' : label;
  });
  const dataValues = stats.map((s) => s.total_minutes);

  const chartData = {
    labels,
    datasets: [
      {
        data: dataValues,
        backgroundColor: chartColors,
        borderColor: stats.map((s, idx) => (
          Number(selectedTaskId) === Number(s.task_id)
            ? 'rgba(13, 110, 253, 1)'
            : chartColors[idx].replace('0.85', '1')
        )),
        borderWidth: stats.map((s) => (
          Number(selectedTaskId) === Number(s.task_id) ? 3 : 1
        )),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        display: stats.length > 0,
        labels: {
          boxWidth: 12,
          padding: 8,
          font: { size: 11 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            const statsItem = stats[context.dataIndex];
            const taskName = statsItem?.task_name || context.label;
            return `${taskName}: ${formatoDuracion(value)} (${pct}%)`;
          },
        },
      },
      datalabels: {
        color: '#333',
        font: { weight: 'bold', size: 10 },
        formatter: (value, context) => {
          const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
          if (total === 0 || (value / total) < 0.05) return '';
          const pct = ((value / total) * 100).toFixed(0);
          return pct + '%';
        },
      },
    },
    onClick: (_, elements) => {
      if (!elements || elements.length === 0) return;
      const index = elements[0].index;
      const selected = stats[index];
      if (!selected?.task_id) return;
      if (Number(selectedTaskId) === Number(selected.task_id)) {
        onSelectTaskId?.(null);
        return;
      }
      onSelectTaskId?.(Number(selected.task_id));
    },
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span className="fw-semibold">{title}</span>
        <div className="d-flex align-items-center gap-2">
          {showPeriodSelector && (
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={effectivePeriodDays}
              onChange={(e) => setInternalPeriodDays(Number(e.target.value))}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
          {totalMinutes > 0 && (
            <span className="text-muted small">
              Total: {formatoDuracion(totalMinutes)}
            </span>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        {loading && (
          <div className="text-center py-4 text-muted">Cargando...</div>
        )}
        {error && (
          <div className="text-center py-4 text-danger">{error}</div>
        )}
        {!loading && !error && stats.length === 0 && (
          <div className="text-center py-4 text-muted">
            No hay tiempo registrado en las tareas para este período.
          </div>
        )}
        {!loading && !error && stats.length > 0 && (
          <div style={{ height: '240px' }}>
            <Pie data={chartData} options={options} />
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default TaskTimePieChart;
